import { useEffect, useMemo, useState } from 'react'
import { useTimeline } from '../context/useTimeline'
import { useWorkspace } from '../context/useWorkspace'
import { isChecklistGate } from '../utils/checklistDependencyState'
import {
  getMemberOutboxSnapshot,
  startMemberOutboxWorker,
  subscribeMemberOutbox,
  type MemberOutboxSnapshot,
} from '../services/memberOutboxService'

const EMPTY_OUTBOX: MemberOutboxSnapshot = { pendingCount: 0, syncingCount: 0, failedIds: [] }

export const useMemberBoard = () => {
  const { elements, checklistItems } = useTimeline()
  const { activeWorkspaceId } = useWorkspace()
  const [outbox, setOutbox] = useState<MemberOutboxSnapshot>(EMPTY_OUTBOX)

  useEffect(() => startMemberOutboxWorker(), [])
  useEffect(() => {
    let cancelled = false
    void getMemberOutboxSnapshot().then((snapshot) => {
      if (!cancelled) setOutbox(snapshot)
    })
    const unsubscribe = subscribeMemberOutbox(setOutbox)
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  const dashboardElements = useMemo(
    () => elements.filter((element) =>
      element.workspaceId === activeWorkspaceId &&
      element.board === 'DASHBOARD',
    ),
    [activeWorkspaceId, elements],
  )

  const checklistCountByElementId = useMemo(() => {
    const next = new Map<string, number>()
    checklistItems.forEach((item) => {
      if (item.workspaceId !== activeWorkspaceId) return
      if (isChecklistGate(item)) return
      const containerId = item.parentContainerId
      if (!containerId) return
      next.set(containerId, (next.get(containerId) ?? 0) + 1)
    })
    dashboardElements.forEach((element) => {
      if (element.checklist.length > 0) {
        next.set(element.id, Math.max(next.get(element.id) ?? 0, element.checklist.length))
      }
    })
    return next
  }, [activeWorkspaceId, checklistItems, dashboardElements])
  const visibleChecklistItems = useMemo(
    () => checklistItems
      .filter((item) => item.workspaceId === activeWorkspaceId)
      .filter((item) => !isChecklistGate(item)),
    [activeWorkspaceId, checklistItems],
  )

  return {
    elements: dashboardElements,
    checklistItems: visibleChecklistItems,
    checklistCountByElementId,
    isSyncing: outbox.pendingCount + outbox.syncingCount > 0,
    failedElementIds: new Set(outbox.failedIds),
  }
}
